/**
 * useRequiredFieldHighlight — Phase 6a.12/F-4 (Spec §11.14.1).
 *
 * Returns a className fragment that paints a SOFT red border on an empty
 * required input. Once the user types a non-empty value, the border
 * reverts to the default `border-input` token. This is a visual hint, not
 * validation — Zod still owns the submit-time gate.
 *
 * Why this instead of `aria-invalid`:
 *   • `aria-invalid` lights up on submitted-and-rejected fields (full red)
 *   • This hook is a pre-validation prompt: "here's what you still need
 *     to fill", at half opacity (`border-destructive/50`)
 *   • The two stack visually distinct (soft prompt vs hard rejection)
 *
 * The `0` literal is a legitimate value and must NOT trigger the highlight.
 * NaN (e.g. a coerced empty number field) IS treated as empty.
 */

import { useFormContext, useWatch } from "react-hook-form";

export function useRequiredFieldHighlight(fieldName: string): string {
  const { control } = useFormContext();
  const value = useWatch({ control, name: fieldName });
  const isEmpty =
    value === undefined ||
    value === null ||
    value === "" ||
    (typeof value === "number" && Number.isNaN(value));
  return isEmpty
    ? "border-destructive/50 focus:border-destructive"
    : "border-input";
}
